/**
 * Balance Sparkline - Miniature time series graph showing balance history
 * 
 * Design: Minimalist, monochrome, ~60-80px wide, ~20-30px tall
 * Shows relative balance changes with baseline reference line
 */

import React, { useMemo } from 'react';
import { BalanceHistoryPoint } from '../services/accountingService';

interface BalanceSparklineProps {
  data: BalanceHistoryPoint[];
  width?: number;
  height?: number;
  className?: string;
}

export const BalanceSparkline: React.FC<BalanceSparklineProps> = ({
  data,
  width = 70,
  height = 24,
  className = '',
}) => {
  const pathData = useMemo(() => {
    if (data.length === 0) return { line: '', baselineY: height / 2, hasChange: false };

    // Extract balance values
    const balances = data.map(point => point.displayBalance);
    const dataMin = Math.min(...balances);
    const dataMax = Math.max(...balances);
    const dataRange = dataMax - dataMin;

    // Use first value as baseline for reference line
    const baseline = balances[0];

    // Handle edge case: all values are exactly the same
    if (dataRange === 0) {
      const y = height / 2;
      const line = data.map((_, i) => {
        const x = (i / Math.max(data.length - 1, 1)) * width;
        return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
      }).join(' ');
      return { line, baselineY: y, hasChange: false };
    }

    // Add 10% padding to range so line doesn't touch edges
    const padding = dataRange * 0.1;
    const min = dataMin - padding;
    const max = dataMax + padding;
    const range = max - min;

    // Calculate baseline position (first data point)
    const baselineY = height - ((baseline - min) / range) * height;

    // Generate path
    const line = data.map((point, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * width;
      const y = height - ((point.displayBalance - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    }).join(' ');

    return { line, baselineY, hasChange: true };
  }, [data, width, height]);

  if (data.length === 0) {
    // Empty state: just show horizontal line at center
    return (
      <svg
        width={width}
        height={height}
        className={`inline-block ${className}`}
        style={{ verticalAlign: 'middle' }}
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.3"
        />
      </svg>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      className={`inline-block ${className}`}
      style={{ verticalAlign: 'middle' }}
    >
      {/* Baseline reference line (first data point value) */}
      {pathData.hasChange && (
        <line
          x1={0}
          y1={pathData.baselineY}
          x2={width}
          y2={pathData.baselineY}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.2"
          strokeDasharray="2,2"
        />
      )}

      {/* Balance line */}
      <path
        d={pathData.line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.8"
      />
    </svg>
  );
};

